/**
 * Stripe minimum charge amounts by currency
 * These are enforced by Stripe to ensure processing fees don't exceed transaction value
 * Source: https://docs.stripe.com/currencies
 */
export const STRIPE_MINIMUM_AMOUNTS = {
  'gbp': 0.30,  // £0.30
  'usd': 0.50,  // $0.50
  'eur': 0.50,  // €0.50
  'aud': 0.50,  // $0.50 AUD
  'cad': 0.50,  // $0.50 CAD
  'nzd': 0.50,  // $0.50 NZD
  'sgd': 0.50,  // $0.50 SGD
  'hkd': 4.00,  // HK$4.00
  'jpy': 50,    // ¥50
  'chf': 0.50,  // CHF 0.50
  'sek': 3.00,  // 3.00 SEK
  'nok': 3.00,  // 3.00 NOK
  'dkk': 2.50,  // 2.50 DKK
  'pln': 2.00,  // 2.00 PLN
  'czk': 15.00, // 15.00 CZK
};

/**
 * Get currency symbol for display
 */
export const getCurrencySymbol = (currency) => {
  const symbols = {
    'gbp': '£',
    'usd': '$',
    'eur': '€',
    'aud': 'A$',
    'cad': 'C$',
    'nzd': 'NZ$',
    'sgd': 'S$',
    'hkd': 'HK$',
    'jpy': '¥',
    'chf': 'CHF ',
    'sek': 'kr',
    'nok': 'kr',
    'dkk': 'kr',
    'pln': 'zł',
    'czk': 'Kč',
  };
  return symbols[currency?.toLowerCase()] || currency?.toUpperCase() || '£';
};

/**
 * Check if amount is below Stripe's minimum for the given currency
 * @param {number} amount - The amount to check
 * @param {string} currency - The currency code (e.g., 'gbp', 'usd')
 * @returns {object} - { isBelowMinimum: boolean, minimumAmount: number, currencySymbol: string }
 */
export const checkStripeMinimum = (amount, currency = 'gbp') => {
  const currencyLower = currency?.toLowerCase() || 'gbp';
  const minimumAmount = STRIPE_MINIMUM_AMOUNTS[currencyLower] || STRIPE_MINIMUM_AMOUNTS['gbp'];
  const invoiceAmount = parseFloat(amount || 0);
  const isBelowMinimum = invoiceAmount < minimumAmount;
  
  return {
    isBelowMinimum,
    minimumAmount,
    currencySymbol: getCurrencySymbol(currencyLower),
    currency: currencyLower.toUpperCase(),
    invoiceAmount
  };
};

/**
 * Get user-friendly error message for below-minimum amounts
 */
export const getMinimumAmountError = (amount, currency = 'gbp') => {
  const check = checkStripeMinimum(amount, currency);
  if (!check.isBelowMinimum) return null;
  
  return `Amount must be at least ${check.currencySymbol}${check.minimumAmount.toFixed(2)} ${check.currency}. This invoice is ${check.currencySymbol}${check.invoiceAmount.toFixed(2)}, which is below the minimum payment amount.`;
};

